import { AnalysisDisplayPublic, AnalysisWithId } from '@livv/models';
import { Button, SxProps, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import AccordionCard from '../components/AccordionCard';
import AnalysisContent from './AnalysisContent';
import KeywordTooltip from './KeywordTooltip';
import LinkedAnalysisModal from './LinkedAnalysisModal';
import { getUrlSearch } from '@components/search/DocumentSearch';
import { LOCAL_STORAGE_LANGUAGE } from '@utils/consts/language';
import useAmplitude from '@utils/hooks/useAmplitude';
import { TrackingEventName } from '@utils/types/amplitude/events';

interface AnalysisCardProps {
    analyses: AnalysisDisplayPublic[];
    focusedAnalysisId?: string;
    isPremiumContent: boolean;
    isFocused?: boolean;
    onPremiumClick?: () => void;
}

const buttonSx: SxProps = {
    fontSize: 'inherit',
    lineHeight: 2,
    mt: 1,
    px: 2,
};

const AnalysisCard: FC<AnalysisCardProps> = ({
    analyses,
    focusedAnalysisId,
    isPremiumContent,
    onPremiumClick,
    isFocused = false,
}) => {
    const cardRef = useRef<HTMLDivElement>(null);
    const { track } = useAmplitude();
    const { t } = useTranslation('decision');
    const {
        query: { search },
    } = useRouter();
    const isInboundSearch = isFocused && Boolean(getUrlSearch(search));

    // At the group, treeTitle and pathKeywords are the same for all analyses
    const {
        pathKeywords,
        pathKeywordsEn,
        theme,
        treeTitle,
        treeTitleEn,
        analysis: firstAnalysis,
    } = analyses[0];

    const defaultShowTranslation: boolean = useMemo(() => {
        if (typeof window === 'undefined' || isInboundSearch) {
            return false;
        }
        const siteLanguage = localStorage.getItem(LOCAL_STORAGE_LANGUAGE);

        return siteLanguage === 'en';
    }, [isInboundSearch]);

    const [showTranslation, setShowTranslation] = useState<boolean>(defaultShowTranslation);

    const [cardPathKeywords, setCardPathKeywords] = useState<string[]>(
        showTranslation && pathKeywordsEn.length ? pathKeywordsEn : pathKeywords,
    );
    const [cardTreeTitle, setCardTreeTitle] = useState<string>(
        showTranslation && treeTitleEn ? treeTitleEn : treeTitle,
    );

    useEffect(() => {
        if (isFocused) {
            cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isFocused]);

    useEffect(() => {
        if (isInboundSearch) setShowTranslation(false);
    }, [isInboundSearch]);

    // Should not happen, just additional security
    if (!cardTreeTitle) {
        return null;
    }

    const title = cardPathKeywords.at(-1);

    const isAtLeastOneAnalysisTranslated = analyses.some(({ analysis }) =>
        Boolean(analysis?.translation?.trim()),
    );
    const showTranslatedVersion = isAtLeastOneAnalysisTranslated && showTranslation;
    const showTranslatedTree = Boolean(showTranslatedVersion && treeTitleEn);
    const displayedKeyword = showTranslatedTree ? firstAnalysis?.keywordEn : firstAnalysis?.keyword;

    const handleTranslationSwitch = (translate: boolean) => {
        setShowTranslation(translate);
        if (showTranslatedTree) {
            setCardPathKeywords(pathKeywordsEn);
            setCardTreeTitle(treeTitleEn);
        } else {
            setCardPathKeywords(pathKeywords);
            setCardTreeTitle(treeTitle);
        }
    };

    const triggerClickEvent = (source: 'button' | 'breadcrumbs') => {
        track({ name: TrackingEventName.LINKED_ANALYSIS_CLICKED, params: { source } });
    };

    return (
        <AccordionCard
            category={treeTitle}
            count={analyses.length > 1 ? analyses.length : undefined}
            isFocused={isFocused}
            onPremiumClick={!isPremiumContent ? onPremiumClick : undefined}
            scrollRef={cardRef}
            sx={{
                '& .MuiAccordionDetails-root': { m: 2, mt: 0 },
                '& .MuiAccordionSummary-content.Mui-expanded': {
                    mt: 0,
                },
                scrollMarginTop: 10,
            }}
            title={title || ''}
        >
            {isPremiumContent ? (
                <>
                    <Typography component="div" variant="body2">
                        {analyses.length > 1 ? (
                            <Box component="ol" sx={{ pl: 2 }}>
                                {analyses.map(({ analysis }) =>
                                    analysis ? (
                                        <li key={analysis?.id}>
                                            <AnalysisContent
                                                analysis={analysis}
                                                isSearchTarget={analysis.id === focusedAnalysisId}
                                                showTranslationVersion={showTranslatedVersion}
                                            />
                                        </li>
                                    ) : null,
                                )}
                            </Box>
                        ) : (
                            <AnalysisContent
                                analysis={analyses[0].analysis as AnalysisWithId}
                                isSearchTarget={analyses[0].analysis?.id === focusedAnalysisId}
                                showTranslationVersion={showTranslatedVersion}
                            />
                        )}
                        {displayedKeyword ? (
                            <KeywordTooltip
                                keyword={displayedKeyword}
                                onClick={() => triggerClickEvent('breadcrumbs')}
                                theme={theme}
                                titles={cardPathKeywords}
                            />
                        ) : null}
                    </Typography>
                    <Typography variant="body2">
                        <LinkedAnalysisModal
                            analysisTitle={title || ''}
                            isCardFocused={isFocused}
                            keyword={displayedKeyword}
                            onButtonClick={() => triggerClickEvent('button')}
                            pathKeywords={cardPathKeywords}
                            t={t}
                            theme={theme}
                            treeTitle={cardTreeTitle}
                        />
                        {isAtLeastOneAnalysisTranslated && (
                            <Button
                                onClick={() => handleTranslationSwitch(!showTranslation)}
                                sx={buttonSx}
                            >
                                {t(`translateAnalysis.${showTranslation ? 'toFR' : 'toEN'}`)}
                            </Button>
                        )}
                    </Typography>
                </>
            ) : null}
        </AccordionCard>
    );
};

export default AnalysisCard;
